import React, { useRef, useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { useS3 } from "../../Hooks/useS3";
import { VideoInfo } from "../models/videoInfo";
import {
  getTotalGapDurationOfAllGapsThatOccurBeforeGivenVideoTimestamp,
  addSecondsToDate,
  isSDLowPlaybackCamera,
} from "../utilities";
import Thumbnail from "./Thumbnail";
import "./styles.css";
import { MotionEvent, MotionEventBarVisibility, MotionEnum } from "../models/motionEvent";
import { GapViewModel } from "../models/gapViewModel";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    motionEvent: {
      height: "100%",
      position: "absolute",
      pointerEvents: "none",
    },
    motionEventsSection: {
      height: "40%",
      backgroundColor: "#333",
      paddingTop: 4,
      paddingBottom: 4,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-around",
    },
    motionEventTypeBarWrapper: {
      height: "25%",
      position: "relative",
    },
    progressBar: {
      height: "100%",
      backgroundColor: "#416ca6",
      position: "absolute",
    },
    progressBarContainer: {
      height: 60,
      cursor: "pointer",
    },
    timelineSection: {
      backgroundColor: "#ccc",
      position: "relative",
      height: "60%",
    },
  })
);

interface Props {
  displayTime: number;
  timezone?: string;
  ISOtoSeconds: (timestamp: string) => number;
  motionEvents: MotionEvent[] | null;
  realTimespanLength: number;
  startDateTime: string;
  videoInfo: VideoInfo | null;
  videoRef: React.RefObject<HTMLVideoElement>;
  videoWidth: number;
  videoAspectRatio: number;
  loadUpdatedPlaylists: (newStartDateTime: Date) => Promise<void>;
  motionEventBarVisibility: MotionEventBarVisibility;
  gapsViewModel: GapViewModel[];
  serialNumber: string;
}

const ProgressBar = ({
  displayTime,
  timezone,
  ISOtoSeconds,
  motionEvents,
  realTimespanLength,
  startDateTime,
  videoInfo,
  videoRef,
  videoWidth,
  videoAspectRatio,
  loadUpdatedPlaylists,
  motionEventBarVisibility,
  gapsViewModel,
  serialNumber,
}: Props) => {
  const progressBarRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const [hoverDisplayTime, setHoverDisplayTime] = useState(0);
  const [mousePositionLeft, setMousePositionLeft] = useState(0);
  const [mousePositionTop, setMousePositionTop] = useState(0);
  const [showThumbnail, setShowThumbnail] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const { s3 } = useS3();
  const classes = useStyles();

  const convertOffsetToVideoSecondsTime = (xOffset: number) => {
    const percentageOfVideoWidth = xOffset / videoWidth;
    const timeOnRealTimespan = percentageOfVideoWidth * realTimespanLength;

    return timeOnRealTimespan;
  };

  const calculateVideoTimeElapsed = (xOffset: number) => {
    if (!videoInfo) {
      return 0;
    }

    let clickedTimeOnRealTimespan = convertOffsetToVideoSecondsTime(xOffset);

    const gapThatReceivedClick = videoInfo.gaps.find(
      (gap) =>
        clickedTimeOnRealTimespan > ISOtoSeconds(gap.startTime) &&
        clickedTimeOnRealTimespan < ISOtoSeconds(gap.startTime) + gap.durationSeconds
    );

    if (gapThatReceivedClick) {
      clickedTimeOnRealTimespan =
        ISOtoSeconds(gapThatReceivedClick.startTime) + gapThatReceivedClick.durationSeconds;
    }

    const totalGapDurationsBeforeWhereUserClicked = videoInfo.gaps.reduce((acc, g) => {
      return ISOtoSeconds(g.startTime) < clickedTimeOnRealTimespan ? acc + g.durationSeconds : acc;
    }, 0);

    const totalVideoTimeElapsed =
      clickedTimeOnRealTimespan - totalGapDurationsBeforeWhereUserClicked;

    return totalVideoTimeElapsed;
  };

  const setPlayheadPositionOnTimeline = (xOffset: number) => {
    try {
      if (!(videoRef.current && videoInfo)) {
        throw new Error("Video data is not available.");
      }

      videoRef.current.currentTime = calculateVideoTimeElapsed(xOffset);
    } catch (error) {
      console.error(error);
    }
  };

  const handleProgressBarClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setPlayheadPositionOnTimeline(event.nativeEvent.offsetX);
    if (!videoInfo) {
      return;
    }
    const offsetSeconds = calculateVideoTimeElapsed(event.nativeEvent.offsetX);
    const newStartDateTime = addSecondsToDate(startDateTime, offsetSeconds);
    loadUpdatedPlaylists(newStartDateTime);
  };

  const timeIsInGap = (timeMS: number) => {
    if (!videoInfo) {
      return false;
    }

    for (const gap of videoInfo.gaps) {
      const gapStartDateTime = new Date(gap.startTime);
      const gapEndDateTime = new Date(gapStartDateTime.getTime() + gap.durationSeconds * 1000);

      if (
        timeMS >= gapStartDateTime.getTime() &&
        timeMS <= gapEndDateTime.getTime() &&
        gap.isVisible
      ) {
        return true;
      }
    }

    return false;
  };

  const handleHover = (evt: any) => {
    try {
      if (!(videoRef.current && videoInfo)) {
        throw new Error("Video data is not available.");
      }

      const hoverPositionSeconds = convertOffsetToVideoSecondsTime(evt.nativeEvent.offsetX);
      const correctedTimestamp = startDateTime.endsWith("Z") ? startDateTime : `${startDateTime}Z`;
      const hoverDateTime = new Date(
        new Date(correctedTimestamp).getTime() + hoverPositionSeconds * 1000
      );
      const hoverInGap = timeIsInGap(hoverDateTime.getTime());

      setShowImage(!hoverInGap);

      setHoverDisplayTime(hoverPositionSeconds);
      setMousePositionLeft(evt.clientX);
      setMousePositionTop(progressBarRef.current.offsetTop);
    } catch (error) {
      console.error(error);
    }
  };

  const filterMotionEventsByType = (type: MotionEnum) => {
    if (!motionEvents || !Array.isArray(motionEvents)) {
      return [];
    }
    const typeInteger = type === "person" ? 0 : type === "vehicle" ? 1 : type === "animal" ? 2 : -1;
    return motionEvents.filter((e) => e.detectionType === typeInteger);
  };

  const generateMotionEventsBar = (motionType: MotionEnum, color: string) => {
    const filteredEvents = filterMotionEventsByType(motionType);
    return filteredEvents.map((event, index) => {
      const marginLeft =
        (ISOtoSeconds(event.artifactRegistrationTime) / realTimespanLength) * videoWidth;
      const eventWidth = (20 / realTimespanLength) * videoWidth;
      // Event will show as 20 seconds long, since the api doesn't yet determine event duration.
      const width = marginLeft + eventWidth > videoWidth ? videoWidth - marginLeft : eventWidth;
      return (
        <div
          id={`${motionType}${index}`}
          key={index}
          style={{
            marginLeft,
            width,
            backgroundColor: color,
          }}
          className={classes.motionEvent}
        ></div>
      );
    });
  };

  return (
    <>
      {showThumbnail && (
        <Thumbnail
          startDateTime={startDateTime}
          realtimeSecondsElapsed={hoverDisplayTime}
          timezone={timezone}
          s3={s3}
          positionLeft={mousePositionLeft}
          positionTop={mousePositionTop}
          videoAspectRatio={videoAspectRatio}
          serialNumber={serialNumber}
          showImage={showImage}
        />
      )}
      {videoInfo ? (
        <div
          id="progress-bar-container"
          style={{ width: videoWidth }}
          className={classes.progressBarContainer}
          onClick={(e) => handleProgressBarClick(e)}
          onMouseMove={(e) => handleHover(e)}
          onMouseEnter={(e) => setShowThumbnail(true)}
          onMouseLeave={(e) => setShowThumbnail(false)}
          ref={progressBarRef}
        >
          <div id="motion-events" className={classes.motionEventsSection}>
            <div
              id="person-events"
              className={classes.motionEventTypeBarWrapper}
              style={{ visibility: !motionEventBarVisibility.person ? "hidden" : "visible" }}
            >
              {generateMotionEventsBar(MotionEnum.Person, "#4f6")}
            </div>
            <div
              id="vehicle-events"
              className={classes.motionEventTypeBarWrapper}
              style={{ visibility: !motionEventBarVisibility.vehicle ? "hidden" : "visible" }}
            >
              {generateMotionEventsBar(MotionEnum.Vehicle, "#f46")}
            </div>
            <div
              id="animal-events"
              className={classes.motionEventTypeBarWrapper}
              style={{ visibility: !motionEventBarVisibility.animal ? "hidden" : "visible" }}
            >
              {generateMotionEventsBar(MotionEnum.Animal, "#fff954")}
            </div>
          </div>
          <div id="timeline" className={classes.timelineSection}>
            <div
              id="progress-bar"
              style={{ width: (displayTime / realTimespanLength) * videoWidth }}
              className={classes.progressBar}
            ></div>
            {gapsViewModel.map((gap, index) => (
              <div
                id={`gap${index}`}
                className={gap.className}
                key={index}
                style={{
                  marginLeft: gap.margin,
                  width: gap.width,
                }}
              ></div>
            ))}
          </div>
        </div>
      ) : (
        <div>Loading...</div>
      )}
    </>
  );
};

export default ProgressBar;
